// What is wrong with an address someone typed, in words a person can act on.
// The server's check (users.ValidEmail, which is the contract's `format: email`
// decoding too) is the rule; this mirrors it so a form can
// say WHY before it asks, and names the slips people actually make: a space, the
// keyboard left in another layout, a missing dot after @. The browser's own
// type=email check said none of this - a bubble that vanishes in a second, and
// the form quietly not sending.

export type EmailProblem =
  | 'empty'
  | 'space'
  | 'no_at'
  | 'many_at'
  | 'not_latin'
  | 'domain'
  | 'shape';

// The server's pattern: a local part, @, and a domain of dot-separated labels -
// no empty label, no trailing dot, no quotes, no whitespace.
const SHAPE = /^[^\s@"]+@[^\s@."]+(\.[^\s@."]+)+$/;

// And the contract's: a body field declared `format: email` is decoded by the
// generated server with Go's net/mail.ParseAddress before any handler sees it,
// and users.ValidEmail holds an account to the same test - so a form refuses
// what it would, or the person reads "could not send" about an address with a
// comma in it (the site's demo form, 19.10). The local part is a dot-atom: the
// RFC 5322 atom characters, dots only between them (never first, last or two in
// a row); each domain label letters, digits and inner hyphens - tighter than
// net/mail, which every real domain is.
const CONTRACT = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// The longest address an account takes (the forms' maxLength, RFC 5321's path).
export const EMAIL_MAX = 254;

/** The first thing wrong with `raw` (trimmed at the ends), or null when the
 *  server will take it. */
export function emailProblem(raw: string): EmailProblem | null {
  const s = raw.trim();
  if (s === '') return 'empty';
  if (/\s/.test(s)) return 'space';
  const at = s.split('@').length - 1;
  if (at === 0) return 'no_at';
  if (at > 1) return 'many_at';
  // Letters outside Latin are almost always a keyboard in the wrong layout
  // ("ьфкштф@" for "marina@"); an address a person really has is Latin.
  if (/[^\x21-\x7e]/.test(s)) return 'not_latin';
  const domain = s.slice(s.indexOf('@') + 1);
  if (!/^[^.]+(\.[^.]+)+$/.test(domain)) return 'domain';
  return s.length <= EMAIL_MAX && SHAPE.test(s) && CONTRACT.test(s) ? null : 'shape';
}
